'use client'

import { useCallback, useEffect, useState } from 'react'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import type { ImportableStop } from '@/lib/import/types'
import { PasteStep } from './PasteStep'
import { type ImportSource, SourcePickerStep } from './SourcePickerStep'
import { StopSelectStep } from './StopSelectStep'
import { UploadStep } from './UploadStep'

interface ImportDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

/**
 * Every stage the import wizard can occupy. The flow fans out from `source`
 * into the file `upload` or `paste` branch, and both converge on the
 * source-agnostic `stops` step.
 */
type Step = 'source' | ImportSource | 'stops'

/** Per-step copy for the dialog description, kept out of the render branch. */
const STEP_DESCRIPTION: Record<Step, string> = {
	source: 'Choose where your route comes from.',
	upload: 'Upload a CSV file to read its stops. From Excel, use File → Save As → CSV.',
	paste: 'Paste itinerary text and we’ll pull the stops out.',
	stops: 'Pick which stops to drop onto your route.',
}

/**
 * Thin orchestrator for the multi-source Import wizard.
 *
 * Holds the step pointer plus the resolved stops/sourceKey. Every step renders
 * in its own file so no single component approaches Biome's cognitive-complexity
 * ceiling. All wizard state resets on close so a re-open always starts clean.
 */
export function ImportDialog({ open, onOpenChange }: ImportDialogProps) {
	const [step, setStep] = useState<Step>('source')
	const [stops, setStops] = useState<ImportableStop[] | null>(null)
	const [sourceKey, setSourceKey] = useState<string | null>(null)

	const close = useCallback(() => onOpenChange(false), [onOpenChange])
	const backToSource = useCallback(() => setStep('source'), [])

	// Reset wizard state on close so re-opening always starts fresh. React
	// batches these with the open=false flip from the parent, which is fine —
	// the content is unmounted.
	useEffect(() => {
		if (open) return
		setStep('source')
		setStops(null)
		setSourceKey(null)
	}, [open])

	/**
	 * Commit extracted stops from the upload/paste branches and advance to the
	 * source-agnostic stops step. The source supplies its own stable key so the
	 * imported-tracker stays scoped to that exact source.
	 */
	const handleExtractedStops = useCallback((extracted: ImportableStop[], key: string) => {
		setStops(extracted)
		setSourceKey(key)
		setStep('stops')
	}, [])

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>Import a route</DialogTitle>
					<DialogDescription>{STEP_DESCRIPTION[step]}</DialogDescription>
				</DialogHeader>

				{step === 'source' && <SourcePickerStep onPick={setStep} />}

				{step === 'upload' && <UploadStep onStops={handleExtractedStops} onBack={backToSource} />}

				{step === 'paste' && <PasteStep onStops={handleExtractedStops} onBack={backToSource} />}

				{step === 'stops' && stops && sourceKey !== null && (
					<StopSelectStep
						stops={stops}
						sourceKey={sourceKey}
						onClose={close}
						onBack={backToSource}
					/>
				)}
			</DialogContent>
		</Dialog>
	)
}
